/**
 * Eric 2026-05-12 — Page "Sources du Chatbot" (admin).
 * Vue d'ensemble de ce que le chatbot RAG voit :
 *   - Stats globales (questions 30j, % avec contexte, feedback +/-)
 *   - Knowledge units par type
 *   - Canaux indexés + nb messages par canal
 *   - Top 10 requêtes récentes ayant remonté un message canal
 */
import { useState, useEffect } from "react";
import axios from "axios";
import {
  Robot,
  Brain,
  Hash,
  VideoCamera,
  ThumbsUp,
  ThumbsDown,
  CircleNotch,
  ChatCircle,
} from "@phosphor-icons/react";
import { useLang } from "../contexts/LanguageContext";

import { API_BASE as API } from "../lib/apiBase";

function StatCard({ icon: Icon, label, value, suffix = "", color = "blue", testid }) {
  const palette = {
    blue: "from-[#002FA7]/10 to-[#002FA7]/5 text-[#002FA7]",
    emerald: "from-teal-100 to-teal-50 text-teal-700",
    amber: "from-amber-100 to-amber-50 text-amber-700",
    red: "from-red-100 to-red-50 text-red-700",
  }[color];
  return (
    <div className={`rounded-xl border border-zinc-200 bg-gradient-to-br ${palette} p-4 flex items-start gap-3`} data-testid={testid}>
      <div className="shrink-0 mt-0.5"><Icon size={22} weight="bold" /></div>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wider font-semibold opacity-70">{label}</p>
        <p className="text-2xl font-bold leading-tight mt-0.5">{value}{suffix}</p>
      </div>
    </div>
  );
}

export default function ChatbotSourcesPage() {
  const { lang } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/api/admin/chatbot-sources`, { withCredentials: true });
        setData(data);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
      }
      setLoading(false);
    };
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-zinc-500" data-testid="chatbot-sources-loading">
        <CircleNotch size={28} className="animate-spin mr-2" />
        {lang === "fr" ? "Chargement des stats…" : "Loading stats…"}
      </div>
    );
  }
  if (error) {
    return <div className="p-6 text-red-600 text-sm" data-testid="chatbot-sources-error">{error}</div>;
  }
  const s = data.summary;
  const pctContext = s.queries_last_30d ? Math.round((s.queries_with_context / s.queries_last_30d) * 100) : 0;
  const pctPositive = s.queries_last_30d ? Math.round((s.queries_positive / s.queries_last_30d) * 100) : 0;

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="chatbot-sources-page">
      <header className="mb-5">
        <h1 className="text-2xl sm:text-3xl font-bold text-zinc-900 inline-flex items-center gap-2">
          <Robot size={28} weight="bold" className="text-[#002FA7]" />
          {lang === "fr" ? "Sources du Chatbot" : "Chatbot Sources"}
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          {lang === "fr"
            ? "Vue admin de tout ce que le chatbot RAG voit aujourd'hui — knowledge units, canaux indexés, transcripts vidéos."
            : "Admin view of what the chatbot RAG sees today — knowledge units, indexed channels, video transcripts."}
        </p>
      </header>

      {/* Stats globales */}
      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard icon={Brain} label={lang === "fr" ? "Unités de connaissances" : "Knowledge units"} value={s.knowledge_units_total} color="blue" testid="stat-knowledge-units" />
        <StatCard icon={Hash} label={lang === "fr" ? "Canaux indexés" : "Indexed channels"} value={`${s.channels_indexed_active}/${s.channels_total}`} color="emerald" testid="stat-channels-indexed" />
        <StatCard icon={ChatCircle} label={lang === "fr" ? "Messages indexés" : "Indexed messages"} value={s.channel_messages_indexed} color="emerald" testid="stat-channel-messages" />
        <StatCard icon={VideoCamera} label={lang === "fr" ? "Vidéos transcrites" : "Transcribed videos"} value={`${s.videos_with_transcript}/${s.videos_total}`} color="amber" testid="stat-videos" />
      </section>

      {/* Stats requêtes 30j */}
      <section className="bg-white border border-zinc-200 rounded-xl p-5 mb-6" data-testid="queries-stats-card">
        <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-500 mb-3">
          {lang === "fr" ? "Activité chatbot (30 derniers jours)" : "Chatbot activity (last 30 days)"}
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-3xl font-bold text-zinc-900" data-testid="queries-total-30d">{s.queries_last_30d}</p>
            <p className="text-xs text-zinc-500 mt-0.5">{lang === "fr" ? "Questions posées" : "Questions asked"}</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-[#002FA7]">{pctContext}%</p>
            <p className="text-xs text-zinc-500 mt-0.5">{lang === "fr" ? "Avec contexte trouvé" : "With context found"}</p>
          </div>
          <div className="flex items-center gap-2">
            <ThumbsUp size={20} weight="bold" className="text-teal-600" />
            <div>
              <p className="text-2xl font-bold text-teal-700">{s.queries_positive}</p>
              <p className="text-xs text-zinc-500">{pctPositive}% {lang === "fr" ? "positifs" : "positive"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ThumbsDown size={20} weight="bold" className="text-red-500" />
            <div>
              <p className="text-2xl font-bold text-red-600">{s.queries_negative}</p>
              <p className="text-xs text-zinc-500">{lang === "fr" ? "négatifs" : "negative"}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 2 colonnes : knowledge by type + channels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <section className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="knowledge-by-type-card">
          <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-500 mb-3">
            <Brain size={14} weight="bold" className="inline mr-1.5 -mt-0.5" />
            {lang === "fr" ? "Unités par type" : "Units by type"}
          </h2>
          {data.knowledge_by_type.length === 0 ? (
            <p className="text-sm text-zinc-400">{lang === "fr" ? "Aucune unité indexée" : "No units indexed"}</p>
          ) : (
            <ul className="space-y-1.5">
              {/* Vincent #420 (Eric 2026-07-06 SPEC STRICK) — Renommage +
                  ordre pipeline d'analyse : 1) Q&R, 2) Scripts de vente,
                  3) Procédures, 4) Objections, 5) Concepts. */}
              {(() => {
                const TYPE_LABEL_FR = {
                  qa: "Q&R",
                  script: "Scripts de vente",
                  sales_script: "Scripts de vente",
                  "sales-script": "Scripts de vente",
                  procedure: "Procédures",
                  objection: "Objections",
                  rebuttal: "Réfutations",
                  concept: "Concepts",
                };
                const TYPE_LABEL_EN = {
                  qa: "Q&A",
                  script: "Sales scripts",
                  sales_script: "Sales scripts",
                  "sales-script": "Sales scripts",
                  procedure: "Procedures",
                  objection: "Objections",
                  rebuttal: "Rebuttals",
                  concept: "Concepts",
                };
                const ORDER = ["qa", "script", "sales_script", "sales-script", "procedure", "objection", "rebuttal", "concept"];
                // Merge all script variants under one line
                const byKey = {};
                for (const row of data.knowledge_by_type) {
                  const t = row.type;
                  const bucket = (t === "sales_script" || t === "sales-script") ? "script" : t;
                  byKey[bucket] = (byKey[bucket] || 0) + (row.count || 0);
                }
                const rows = ORDER.filter((t) => byKey[t] !== undefined && !(t === "sales_script" || t === "sales-script"))
                  .map((t) => ({ type: t, count: byKey[t] }));
                // Add any type unknown from ORDER at the end
                for (const t of Object.keys(byKey)) {
                  if (!ORDER.includes(t)) rows.push({ type: t, count: byKey[t] });
                }
                return rows.map((row) => (
                  <li key={row.type} className="flex items-center justify-between text-sm border-b border-zinc-100 pb-1.5" data-testid={`ku-type-${row.type}`}>
                    <span className="text-zinc-700">{(lang === "fr" ? TYPE_LABEL_FR : TYPE_LABEL_EN)[row.type] || row.type}</span>
                    <span className="font-bold text-[#002FA7]">{row.count}</span>
                  </li>
                ));
              })()}
            </ul>
          )}
        </section>

        <section className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="channels-stats-card">
          <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-500 mb-3">
            <Hash size={14} weight="bold" className="inline mr-1.5 -mt-0.5" />
            {lang === "fr" ? "Canaux Messagerie" : "Messaging channels"}
          </h2>
          <ul className="space-y-1.5">
            {data.channels.map((c) => (
              <li key={c.id} className="flex items-center justify-between text-sm border-b border-zinc-100 pb-1.5" data-testid={`channel-stats-${c.slug}`}>
                <span className="flex items-center gap-2 min-w-0">
                  <span>{c.emoji}</span>
                  <span className="truncate text-zinc-700">{c.name}</span>
                  {c.indexed_by_chatbot && (
                    <span className="shrink-0 text-[10px] px-1.5 py-0.5 bg-teal-100 text-teal-800 rounded font-semibold">
                      {lang === "fr" ? "INDEXÉ" : "INDEXED"}
                    </span>
                  )}
                </span>
                <span className="text-xs text-zinc-500 shrink-0">
                  <span className="font-semibold text-teal-700">{c.messages_indexed}</span>
                  <span className="text-zinc-400"> / {c.messages_total}</span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      {/* Top 10 recent channel hits */}
      <section className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="recent-channel-hits-card">
        <h2 className="text-sm font-bold uppercase tracking-wide text-zinc-500 mb-3">
          {lang === "fr"
            ? "Requêtes récentes ayant utilisé un canal indexé"
            : "Recent queries that used an indexed channel"}
        </h2>
        {data.recent_channel_hits.length === 0 ? (
          <p className="text-sm text-zinc-400">
            {lang === "fr"
              ? "Aucune requête n'a encore exploité un message de canal. Activez l'indexation sur un canal et postez-y du contenu pour commencer."
              : "No query has used a channel message yet. Enable indexing on a channel and post content to start."}
          </p>
        ) : (
          <ul className="space-y-3">
            {data.recent_channel_hits.map((q) => (
              <li key={q.query_id} className="border-l-2 border-[#002FA7]/40 pl-3 text-sm" data-testid={`hit-${q.query_id}`}>
                <p className="font-medium text-zinc-900">« {q.question} »</p>
                <p className="text-[11px] text-zinc-500 mt-1">
                  {q.created_at ? new Date(q.created_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-CA") : "—"}
                  {q.feedback_rating === "positive" && <ThumbsUp size={11} weight="bold" className="inline ml-2 text-teal-600" />}
                  {q.feedback_rating === "negative" && <ThumbsDown size={11} weight="bold" className="inline ml-2 text-red-500" />}
                </p>
                {q.channel_sources?.map((s, i) => (
                  <p key={i} className="text-[12px] text-zinc-600 mt-1 italic">
                    → <span className="font-semibold">#{s.channel_name}</span> : {s.preview}…
                  </p>
                ))}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
